package org.vreo.devicehub.features.core.data

import io.ktor.client.HttpClient
import io.ktor.client.engine.HttpClientEngine
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

import org.vreo.devicehub.features.core.data.appLogger.AppLogger

private const val TAG = "HttpClient"
/**
 * A factory object for creating configured [HttpClient] instances.
 * This factory sets up common plugins and configurations for HTTP communication,
 * including JSON content negotiation, timeouts, and logging.
 */
object HttpClientFactory {

    /**
     * Creates a new [HttpClient] instance with predefined configurations.
     *
     * The client is configured with:
     * - **Content Negotiation**: Automatically handles JSON serialization and deserialization
     * using `kotlinx.serialization`. It's set to `ignoreUnknownKeys = true` for robustness
     * when dealing with evolving API responses.
     * - **HTTP Timeout**: Configures socket and request timeouts to 20 seconds each,
     * preventing indefinite waits for responses.
     * - **Logging**: Enables comprehensive logging of HTTP requests and responses to the console.
     * - **Default Request Headers**: Sets the `Content-Type` header to `application/json`
     * for all outgoing requests by default.
     *
     * @param engine The [HttpClientEngine] to use for making HTTP requests.
     * @return A configured [HttpClient] instance ready for making network requests.
     */
    fun create(engine: HttpClientEngine): HttpClient {
        return HttpClient(engine) {
            install(ContentNegotiation) {
                json(
                    json = Json {
                        // Ignore JSON keys that are not defined in data classes
                        ignoreUnknownKeys = true
                        isLenient = true
                        encodeDefaults = true
                    }
                )
            }
            install(HttpTimeout) {
                // Maximum time to wait for a socket to connect or for data to be available on a socket.
                socketTimeoutMillis = 10_000L

                // Maximum time to wait for the entire request, including connecting and receiving data.
                requestTimeoutMillis = 10_000L
            }
            install(Logging) {
                logger = object : Logger {
                    override fun log(message: String) {
                        AppLogger.info(
                            tag = TAG,
                            message = message
                        )
                    }
                }
                level = LogLevel.INFO
            }
            defaultRequest {
                contentType(ContentType.Application.Json)
            }
        }
    }
}